import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FolderSimple, FolderPlus, UploadSimple, DownloadSimple, Trash, MagnifyingGlass,
  CaretLeft, CaretRight, FileText, FilePdf, FileXls, FileDoc, Image as ImageIcon,
  PencilSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AutoUploadInstellingen } from "@/components/AutoUploadInstellingen";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const grootte = (b) => {
  if (!b) return "0 kB";
  if (b < 1024 * 1024) return `${Math.max(Math.round(b / 1024), 1)} kB`;
  return `${(b / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
};

const stamp = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("nl-BE", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

const Icoon = ({ naam, type }) => {
  const ext = (naam || "").split(".").pop().toLowerCase();
  if ((type || "").startsWith("image")) return <ImageIcon size={18} weight="bold" className="text-[#7C3AED]" />;
  if (ext === "pdf") return <FilePdf size={18} weight="bold" className="text-[#E53E3E]" />;
  if (["xls", "xlsx", "csv"].includes(ext)) return <FileXls size={18} weight="bold" className="text-[#1C7C54]" />;
  if (["doc", "docx"].includes(ext)) return <FileDoc size={18} weight="bold" className="text-[#002FA7]" />;
  return <FileText size={18} weight="bold" className="text-muted-foreground" />;
};

export const BestandenTab = ({ canBeheren }) => {
  const [mappen, setMappen] = useState({ filialen: [], algemeen: [], beschikbaar: [] });
  const [filiaal, setFiliaal] = useState(null);   // {filiaal_id, filiaal_naam, submappen}
  const [submap, setSubmap] = useState(null);     // {id, naam, algemeen?}
  const [bestanden, setBestanden] = useState([]);
  const [zoek, setZoek] = useState("");
  const [bezig, setBezig] = useState(false);
  const [nieuweMap, setNieuweMap] = useState(null);   // {filiaal, naam}
  const [nieuweAlgMap, setNieuweAlgMap] = useState("");
  const inputRef = useRef(null);

  const laadMappen = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bestanden/mappen`);
      setMappen({
        filialen: data.filialen || [],
        algemeen: data.algemeen || [],
        beschikbaar: data.beschikbaar || [],
      });
      setFiliaal((f) => (f ? (data.filialen || []).find((x) => x.filiaal_id === f.filiaal_id) || null : null));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mappen laden mislukt");
    }
  }, []);

  const laadBestanden = useCallback(async () => {
    if (!filiaal && !submap) { setBestanden([]); return; }
    try {
      const params = new URLSearchParams();
      if (filiaal) params.set("filiaal_id", filiaal.filiaal_id);
      if (submap) params.set("map_id", submap.id);
      if (zoek) params.set("zoek", zoek);
      const { data } = await axios.get(`${API}/bestanden?${params}`);
      setBestanden(data.bestanden || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bestanden laden mislukt");
    }
  }, [filiaal, submap, zoek]);

  useEffect(() => { laadMappen(); }, [laadMappen]);
  useEffect(() => { laadBestanden(); }, [laadBestanden]);

  const upload = async (lijst) => {
    if (!lijst?.length) return;
    setBezig(true);
    let gelukt = 0;
    for (const f of Array.from(lijst)) {
      const fd = new FormData();
      fd.append("file", f);
      if (filiaal) fd.append("filiaal_id", filiaal.filiaal_id);
      if (submap && submap.id !== "los") fd.append("map_id", submap.id);
      try {
        await axios.post(`${API}/bestanden/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        gelukt += 1;
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || "upload mislukt"}`);
      }
    }
    setBezig(false);
    if (gelukt) toast.success(`${gelukt} bestand(en) opgeslagen`);
    if (inputRef.current) inputRef.current.value = "";
    laadBestanden();
    laadMappen();
  };

  const download = async (b) => {
    try {
      const res = await axios.get(`${API}/bestanden/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.naam;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download mislukt");
    }
  };

  const verwijder = async (b) => {
    if (!window.confirm(`"${b.naam}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/bestanden/${b.id}`);
      toast.success("Bestand verwijderd");
      laadBestanden();
      laadMappen();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const omschrijf = async (b) => {
    const tekst = window.prompt("Omschrijving / notitie", b.omschrijving || "");
    if (tekst === null) return;
    try {
      await axios.patch(`${API}/bestanden/${b.id}`, { omschrijving: tekst });
      laadBestanden();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opslaan mislukt");
    }
  };

  const maakMap = async (naamRuw, filiaalId, resetter) => {
    const naam = (naamRuw || "").trim();
    if (!naam) return;
    try {
      await axios.post(`${API}/bestanden/map`, { filiaal_id: filiaalId || null, naam });
      resetter("");
      toast.success(`Map "${naam}" aangemaakt`);
      laadMappen();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Map aanmaken mislukt");
    }
  };

  const verwijderMap = async (m, algemeen = false) => {
    const metBestanden = m.aantal > 0 && window.confirm(
      `Map "${m.naam}" bevat ${m.aantal} bestand(en).\n\nOK = map én bestanden verwijderen\nAnnuleren = enkel de map verwijderen, bestanden blijven bewaard`,
    );
    if (m.aantal === 0 && !window.confirm(`Map "${m.naam}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/bestanden/map/${m.id}?met_bestanden=${metBestanden}`);
      if (submap?.id === m.id) setSubmap(null);
      toast.success("Map verwijderd");
      laadMappen();
      laadBestanden();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
    }
    if (algemeen) setFiliaal(null);
  };

  const voegFiliaalToe = async (filiaalId) => {
    try {
      await axios.post(`${API}/bestanden/filiaal`, { filiaal_id: filiaalId });
      laadMappen();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toevoegen mislukt");
    }
  };

  const verwijderFiliaalMap = async (m) => {
    if (!window.confirm(`Map van "${m.filiaal_naam}" uit de lijst halen?`)) return;
    try {
      await axios.delete(`${API}/bestanden/filiaal/${m.filiaal_id}`);
      toast.success("Filiaalmap verwijderd");
      laadMappen();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const MapChip = ({ m, onOpen, onDel }) => (
    <span className="flex items-center bg-white border border-border rounded-sm">
      <button
        onClick={onOpen}
        className="h-9 pl-2 pr-2.5 flex items-center gap-1.5 text-xs font-bold hover:text-[#002FA7]"
        data-testid={`bestanden-submap-${m.id}`}
      >
        <FolderSimple size={16} weight="fill" className="text-[#002FA7]" />
        {m.naam}
        <span className="text-[10px] text-muted-foreground">({m.aantal})</span>
      </button>
      {canBeheren && (
        <button
          onClick={onDel}
          className="h-9 w-8 flex items-center justify-center text-destructive border-l border-border"
          title="Map verwijderen"
          data-testid={`bestanden-submap-del-${m.id}`}
        >
          <Trash size={13} />
        </button>
      )}
    </span>
  );

  /* ─────────── Overzicht: elk filiaal onder elkaar ─────────── */
  if (!filiaal && !submap) {
    return (
      <div className="space-y-3" data-testid="bestanden-overzicht">
        <div className="bg-white border border-border rounded-sm px-4 py-3">
          <h2 className="font-bold text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>Bestanden</h2>
          <p className="text-xs text-muted-foreground">
            Elk filiaal heeft zijn eigen map. Open een map om bestanden te bekijken, te downloaden,
            toe te voegen of te verwijderen (max 25 MB per bestand).
          </p>
        </div>

        {canBeheren && <AutoUploadInstellingen />}

        {mappen.filialen.map((m) => (          <div key={m.filiaal_id} className="bg-white border border-border rounded-sm" data-testid={`bestanden-filiaal-blok-${m.filiaal_id}`}>
            <div className="flex items-center">
              <button
                onClick={() => { setFiliaal(m); setSubmap(null); setZoek(""); }}
                className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 text-left hover:bg-[#EAF1FF]/50 transition-colors"
                data-testid={`bestanden-filiaal-${m.filiaal_id}`}
              >
                <FolderSimple size={28} weight="fill" className="text-[#002FA7] shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm truncate">{m.filiaal_naam}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {m.aantal} bestand{m.aantal === 1 ? "" : "en"} · {grootte(m.grootte)}
                    {m.submappen.length ? ` · ${m.submappen.length} map(pen)` : ""}
                  </p>
                </div>
                <CaretRight size={16} weight="bold" className="text-muted-foreground shrink-0" />
              </button>
              {canBeheren && (
                <button
                  onClick={() => verwijderFiliaalMap(m)}
                  className="h-10 w-10 mr-2 flex items-center justify-center rounded-sm text-destructive hover:bg-destructive/10"
                  title="Deze filiaalmap verbergen"
                  data-testid={`bestanden-filiaal-del-${m.filiaal_id}`}
                >
                  <Trash size={15} />
                </button>
              )}
            </div>
            {m.submappen.length > 0 && (
              <div className="px-4 py-2 border-t border-border bg-muted/20 flex flex-wrap items-center gap-2">
                {m.submappen.map((s) => (
                  <MapChip
                    key={s.id}
                    m={s}
                    onOpen={() => { setFiliaal(m); setSubmap(s); setZoek(""); }}
                    onDel={() => verwijderMap(s)}
                  />
                ))}
              </div>
            )}
          </div>
        ))}

        {canBeheren && mappen.beschikbaar.length > 0 && (
          <div className="bg-white border border-dashed border-border rounded-sm px-4 py-3 flex flex-wrap items-center gap-2" data-testid="bestanden-filiaal-toevoegen">
            <span className="text-xs font-semibold text-muted-foreground">Filiaalmap toevoegen:</span>
            {mappen.beschikbaar.map((f) => (
              <button
                key={f.filiaal_id}
                onClick={() => voegFiliaalToe(f.filiaal_id)}
                className="h-9 px-2.5 flex items-center gap-1.5 rounded-sm border border-border text-xs font-bold hover:border-[#002FA7] hover:text-[#002FA7]"
                data-testid={`bestanden-filiaal-add-${f.filiaal_id}`}
              >
                <FolderPlus size={15} weight="bold" />
                {f.filiaal_naam}
              </button>
            ))}
          </div>
        )}

        {/* ── Eigen mappen (niet aan een filiaal gebonden) ── */}
        <div className="bg-white border border-border rounded-sm" data-testid="bestanden-algemeen-blok">
          <div className="px-4 py-3 border-b border-border">
            <p className="font-bold text-sm">Andere bestanden</p>
            <p className="text-[11px] text-muted-foreground">
              Eigen mappen die niet bij één filiaal horen (bv. verzekeringen, contracten, handleidingen).
            </p>
          </div>
          <div className="px-4 py-2 flex flex-wrap items-center gap-2">
            {mappen.algemeen.map((m) => (
              <MapChip
                key={m.id}
                m={m}
                onOpen={() => { setFiliaal(null); setSubmap({ ...m, algemeen: true }); setZoek(""); }}
                onDel={() => verwijderMap(m, true)}
              />
            ))}
            {mappen.algemeen.length === 0 && !canBeheren && (
              <p className="text-xs text-muted-foreground">Nog geen mappen.</p>
            )}
            {canBeheren && (
              <span className="flex items-center gap-1.5">
                <Input
                  value={nieuweAlgMap}
                  onChange={(e) => setNieuweAlgMap(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && maakMap(nieuweAlgMap, null, setNieuweAlgMap)}
                  placeholder="Nieuwe map..."
                  className="h-9 rounded-sm text-xs w-40"
                  data-testid="bestanden-alg-map-input"
                />
                <Button
                  onClick={() => maakMap(nieuweAlgMap, null, setNieuweAlgMap)}
                  variant="outline"
                  className="rounded-sm h-9"
                  data-testid="bestanden-alg-map-btn"
                >
                  <FolderPlus size={15} weight="bold" />
                </Button>
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ─────────── In een map ─────────── */
  const titel = filiaal
    ? `${filiaal.filiaal_naam}${submap ? ` › ${submap.naam}` : ""}`
    : `Andere bestanden › ${submap.naam}`;

  return (
    <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid="bestanden-map">
      <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2">
        <button
          onClick={() => {
            if (submap && filiaal) setSubmap(null);
            else { setSubmap(null); setFiliaal(null); }
          }}
          className="h-9 px-2 flex items-center gap-1 rounded-sm border border-border text-xs font-bold"
          data-testid="bestanden-terug"
        >
          <CaretLeft size={14} weight="bold" /> Terug
        </button>
        <div className="min-w-0">
          <h2 className="font-bold text-sm truncate" style={{ fontFamily: "Chivo, sans-serif" }}>{titel}</h2>
          <p className="text-[11px] text-muted-foreground">
            {bestanden.length} bestand{bestanden.length === 1 ? "" : "en"} · nieuwste bovenaan
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
              placeholder="Zoek bestand..."
              className="h-9 pl-7 rounded-sm text-xs w-36 sm:w-48"
              data-testid="bestanden-zoek"
            />
          </div>
          {canBeheren && (
            <>
              <input
                ref={inputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => upload(e.target.files)}
                data-testid="bestanden-file-input"
              />
              <Button
                onClick={() => inputRef.current?.click()}
                disabled={bezig}
                className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white"
                data-testid="bestanden-upload-btn"
              >
                <UploadSimple size={16} weight="bold" className="mr-1.5" />
                {bezig ? "Uploaden..." : "Uploaden"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Submappen van dit filiaal */}
      {filiaal && !submap && (filiaal.submappen.length > 0 || canBeheren) && (
        <div className="px-4 py-3 border-b border-border bg-muted/20 flex flex-wrap items-center gap-2">
          {filiaal.submappen.map((m) => (
            <MapChip key={m.id} m={m} onOpen={() => setSubmap(m)} onDel={() => verwijderMap(m)} />
          ))}
          {canBeheren && (
            <span className="flex items-center gap-1.5">
              <Input
                value={nieuweMap?.filiaal === filiaal.filiaal_id ? nieuweMap.naam : ""}
                onChange={(e) => setNieuweMap({ filiaal: filiaal.filiaal_id, naam: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && maakMap(nieuweMap?.naam, filiaal.filiaal_id, setNieuweMap)}
                placeholder="Nieuwe map..."
                className="h-9 rounded-sm text-xs w-36"
                data-testid="bestanden-nieuwe-map-input"
              />
              <Button
                onClick={() => maakMap(nieuweMap?.naam, filiaal.filiaal_id, setNieuweMap)}
                variant="outline"
                className="rounded-sm h-9"
                data-testid="bestanden-nieuwe-map-btn"
              >
                <FolderPlus size={15} weight="bold" />
              </Button>
            </span>
          )}
        </div>
      )}

      {/* Bestandenlijst */}
      {bestanden.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground text-center">Nog geen bestanden in deze map.</p>
      ) : (
        <div>
          <div className="hidden lg:grid grid-cols-[minmax(180px,2fr)_minmax(140px,1.5fr)_130px_90px_minmax(120px,1fr)_120px] bg-muted/30 border-b border-border text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <span className="px-2 py-1.5">Bestand</span>
            <span className="px-2 py-1.5 border-l border-border">Omschrijving</span>
            <span className="px-2 py-1.5 border-l border-border">Datum en uur</span>
            <span className="px-2 py-1.5 border-l border-border text-right">Grootte</span>
            <span className="px-2 py-1.5 border-l border-border">Door</span>
            <span className="px-2 py-1.5 border-l border-border text-right">Acties</span>
          </div>
          {bestanden.map((b) => (
            <div
              key={b.id}
              className="grid grid-cols-1 lg:grid-cols-[minmax(180px,2fr)_minmax(140px,1.5fr)_130px_90px_minmax(120px,1fr)_120px] border-b border-border items-center hover:bg-[#EAF1FF]/40"
              data-testid={`bestand-rij-${b.id}`}
            >
              <span className="px-2 py-2 flex items-center gap-2 min-w-0">
                <Icoon naam={b.naam} type={b.content_type} />
                <button
                  onClick={() => download(b)}
                  className="text-sm font-semibold truncate text-left hover:underline"
                  data-testid={`bestand-naam-${b.id}`}
                >
                  {b.naam}
                </button>
              </span>
              <span className="px-2 py-1 lg:py-2 text-xs text-muted-foreground truncate lg:border-l border-border">
                {b.omschrijving || "–"}
              </span>
              <span className="px-2 py-1 lg:py-2 text-xs tabular-nums lg:border-l border-border">
                {stamp(b.created_at)}
              </span>
              <span className="px-2 py-1 lg:py-2 text-xs tabular-nums lg:text-right lg:border-l border-border">
                {grootte(b.grootte)}
              </span>
              <span className="px-2 py-1 lg:py-2 text-xs text-muted-foreground truncate lg:border-l border-border">
                {b.geupload_door || "–"}
              </span>
              <span className="px-2 py-2 flex lg:justify-end gap-1.5 lg:border-l border-border">
                <button
                  onClick={() => download(b)}
                  className="h-8 w-8 flex items-center justify-center rounded-sm border border-border"
                  title="Downloaden"
                  data-testid={`bestand-download-${b.id}`}
                >
                  <DownloadSimple size={14} />
                </button>
                {canBeheren && (
                  <>
                    <button
                      onClick={() => omschrijf(b)}
                      className="h-8 w-8 flex items-center justify-center rounded-sm border border-border"
                      title="Omschrijving"
                      data-testid={`bestand-note-${b.id}`}
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      onClick={() => verwijder(b)}
                      className="h-8 w-8 flex items-center justify-center rounded-sm border border-border text-destructive"
                      title="Verwijderen"
                      data-testid={`bestand-del-${b.id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
